import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  ButtonGroup,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers';
import { ROUTES } from '../../config/constants';
import { useBookings } from '../../hooks/booking/useBookings';
import { fetchBookings } from './BookingService';
import { useAuth } from '../../hooks';
import { Booking } from '../../types/booking';
import ResourceSelect from '../../components/bookings/ResourceSelect';
import { useUnavailableDates } from '../../hooks/unavailable-dates/useUnavailableDates';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useCustomers } from '../../hooks/customer/useCustomers';
import { Customer } from '../../types/customer';
import { useForm } from 'react-hook-form';
import { CreateBookingDTO } from '../../types/booking';
import { useBookingActions } from '../../hooks/booking/useBookingActions';
import ExternalEventDrawer from './ExternalEventDrawer';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';

const EVENT_COLORS = {
  normal: { bg: '#8B5CF6', border: '#7C3AED' },
  recurring: { bg: '#F59E0B', border: '#D97706' },
  external: { bg: '#10B981', border: '#059669' },
};

interface BookingCalendarProps {
  onSlotSelect: (start: Date, end: Date) => void;
  onDateChange?: (start: Date, end: Date) => void;
  selectedResource?: number;
  onResourceChange?: (resourceId: number | undefined) => void;
}

const VIEW_MAPPING = {
  month: 'dayGridMonth',
  week: 'timeGridWeek',
  day: 'timeGridDay',
} as const;

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  onSlotSelect,
  onDateChange,
  selectedResource,
  onResourceChange = () => {},
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const calendarRef = useRef<any>(null);
  const [title, setTitle] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [currentView, setCurrentView] = useState<'month' | 'week' | 'day'>('week');
  const { role, clientResourceId } = useAuth();
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: dayjs().startOf('week').toISOString(),
    end: dayjs().endOf('week').toISOString(),
  });
  const [createLoading, setCreateLoading] = useState(false);

  const methods = useForm<CreateBookingDTO>({
    defaultValues: {
      resourceId: 0,
      customerId: 0,
      serviceId: [],
      title: '',
      description: '',
      startTime: '',
      endTime: '',
      recurrenceRule: '',
      googleEventId: '',
    },
  });

  const { watch, setValue, reset } = methods;
  const selectedResourceId = watch('resourceId');

  const [_selectedHolidayDate, setSelectedHolidayDate] = useState<Date | null>(null);
  const [_confirmHolidayOpen, setConfirmHolidayOpen] = useState(false);

  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const { createBooking } = useBookingActions();

  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCustomerSearch(customerSearch), 500);
    return () => clearTimeout(t);
  }, [customerSearch]);

  const { customers, isLoading: customersLoading } = useCustomers(
    1,
    25,
    'firstName',
    'ASC',
    debouncedCustomerSearch
  );

  const [selectedCustomerForExternal, setSelectedCustomerForExternal] = useState<Customer | null>(
    null
  );

  const handleDayHeaderClick = (event: Event) => {
    const target = event.currentTarget as HTMLElement;
    const dateStr = target.getAttribute('data-date');
    if (dateStr) {
      setSelectedHolidayDate(new Date(dateStr));
      setConfirmHolidayOpen(true);
    }
  };

  const organizationId = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation?.id
  );

  const { unavailableDates } = useUnavailableDates(
    organizationId || '',
    selectedResource?.toString() ?? '',
    dateRange.start,
    dateRange.end
  );

  const { isLoading, error, refetch, bookings, externalEvents } = useBookings();

  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    onSlotSelect(slotInfo.start, slotInfo.end);
  };

  const handleViewChange = async (
    _: React.MouseEvent<HTMLElement>,
    newView: 'month' | 'week' | 'day' | null
  ) => {
    if (newView) {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        const today = new Date();
        calendarApi.changeView(VIEW_MAPPING[newView], today);
        setTitle(calendarApi.view.title);
        setCurrentView(newView);
        setSelectedDate(today);
        if (onDateChange) {
          onDateChange(
            new Date(calendarApi.view.currentStart),
            new Date(calendarApi.view.currentEnd)
          );
          setDateRange({
            start: calendarApi.view.currentStart.toISOString(),
            end: calendarApi.view.currentEnd.toISOString(),
          });
        }
      }
    }
  };

  const handleNavigate = async (action: 'prev' | 'next' | 'today') => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    switch (action) {
      case 'today':
        calendarApi.today();
        break;
      case 'prev':
        calendarApi.prev();
        break;
      case 'next':
        calendarApi.next();
        break;
    }

    setTitle(calendarApi.view.title);
    setSelectedDate(new Date(calendarApi.view.currentStart));
    if (onDateChange) {
      onDateChange(new Date(calendarApi.view.currentStart), new Date(calendarApi.view.currentEnd));
      setDateRange({
        start: calendarApi.view.currentStart.toISOString(),
        end: calendarApi.view.currentEnd.toISOString(),
      });
    }
  };

  const fetchBookingsForView = async (calendarApi: any) => {
    try {
      await fetchBookings(
        role,
        clientResourceId,
        selectedResource,
        {
          start: calendarApi.view.currentStart.toISOString(),
          end: calendarApi.view.currentEnd.toISOString(),
        },
        refetch
      );
    } catch (err) {
      console.error('Error fetching bookings:', err);
    }
  };

  const handleEventClick = (info: any) => {
    info.jsEvent.preventDefault();
    const isExternal = info.event.extendedProps.isExternal;

    if (isExternal) {
      setSelectedEvent(info.event);
      reset({
        resourceId: clientResourceId || 0,
        customerId: 0,
        serviceId: [],
        title: info.event.title,
        description: info.event.extendedProps.description || '',
        startTime: info.event.start.toISOString(),
        endTime: info.event.end.toISOString(),
        recurrenceRule: '',
        googleEventId: info.event.id.replace('ext-', ''),
      });
      setIsDrawerOpen(true);
    } else {
      const bookingId = info.event.id;
      const recurrenceId = info.event.extendedProps.recurrenceId || null;
      navigate(`${ROUTES.BOOKINGS}/${bookingId}`, {
        state: { recurrenceId },
      });
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedEvent(null);
  };

  const handleDateChange = async (date: Date | null) => {
    if (!date) return;

    setSelectedDate(date);
    setOpen(false);

    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.gotoDate(date);
    }
  };

  const handleCreateBooking = async (data: CreateBookingDTO) => {
    setCreateLoading(true);
    try {
      await createBooking(data);

      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        await fetchBookingsForView(calendarApi);
      }

      return true;
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    } finally {
      setCreateLoading(false);
    }
  };

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (selectedDate) {
      calendarApi.gotoDate(selectedDate.toISOString());
    }

    const fetchAndUpdate = async () => {
      try {
        await fetchBookingsForView(calendarApi);
      } catch (err) {
        console.error('Error fetching bookings for view:', err);
      }
    };

    fetchAndUpdate();
  }, [selectedDate, selectedResource, calendarRef.current]);

  useEffect(() => {
    const headers = document.querySelectorAll('.fc-col-header-cell');

    headers.forEach(header => {
      header.addEventListener('click', handleDayHeaderClick);
    });

    return () => {
      headers.forEach(header => {
        header.removeEventListener('click', handleDayHeaderClick);
      });
    };
  }, [calendarRef]);

  const getEventColor = (recurrenceId: number | null) => {
    return recurrenceId ? EVENT_COLORS.recurring : EVENT_COLORS.normal;
  };

  const events = bookings.map((booking: Booking) => {
    const color = getEventColor(booking.recurrenceId ?? null);
    return {
      id: booking.id.toString(),
      title: `Booking with ${booking.resource.name} for ${booking.customer.firstName}`,
      start: booking.startTime,
      end: booking.endTime,
      resourceId: booking.resourceId.toString(),
      backgroundColor: color.bg,
      borderColor: color.border,
      textColor: '#FFFFFF',
      extendedProps: {
        resource: booking.resource,
        customer: booking.customer,
        recurrenceId: booking.recurrenceId,
        meetingLink: booking.meetingLink,
      },
    };
  });

  const unavailableEvents = unavailableDates?.map(date => ({
    id: `unavailable-${date.id}`,
    title: date.description || 'Unavailable',
    start: dayjs(date.bookedDate).startOf('day').toISOString(),
    end: dayjs(date.bookedDate).endOf('day').toISOString(),
    display: 'background',
    backgroundColor: '#3D3B40', // Charcoal gray
    borderColor: '#A14EBF', // Soft violet-rose to echo your purple theme
    textColor: '#E5E5E5',
    allDay: true,
    extendedProps: {
      isUnavailable: true,
    },
  }));

  const transformExternalEvents = (externalEvents: any[]) => {
    if (selectedResource !== clientResourceId) {
      return [];
    }
    return (
      externalEvents?.map(event => ({
        id: `ext-${event.id}`,
        title: event.summary || 'External Event',
        start: event.start.dateTime || event.start.date,
        end: event.end.dateTime || event.end.date,
        backgroundColor: EVENT_COLORS.external.bg,
        borderColor: EVENT_COLORS.external.border,
        textColor: '#FFFFFF',
        extendedProps: {
          isExternal: true,
          description: event.description,
          organizer: event.organizer?.email,
          isCacxiEvent: event.extendedProperties?.private?.cacxi === 'true',
        },
      })) || []
    );
  };

  const externalCalendarEvents = transformExternalEvents(externalEvents);
  // const allEvents = [...events, ...externalCalendarEvents];
  const allEvents = [...events, ...externalCalendarEvents, ...unavailableEvents];

  {
    error && (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Paper>
    );
  }

  return isLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
      <CircularProgress />
    </Box>
  ) : (
    <Paper
      elevation={0}
      sx={{
        pt: 3,
        '& .fc': {
          '--fc-border-color': theme.palette.divider,
          '--fc-button-text-color': theme.palette.text.primary,
          '--fc-button-bg-color': theme.palette.background.paper,
          '--fc-button-border-color': theme.palette.divider,
          '--fc-button-hover-bg-color': theme.palette.action.hover,
          '--fc-button-hover-border-color': theme.palette.primary.main,
          '--fc-button-active-bg-color': theme.palette.primary.main,
          '--fc-button-active-border-color': theme.palette.primary.dark,
          '--fc-today-bg-color': `${theme.palette.primary.main}15`,
          '--fc-event-bg-color': theme.palette.primary.main,
          '--fc-event-border-color': theme.palette.primary.dark,
          '--fc-page-bg-color': theme.palette.background.paper,
          height: 'calc(100vh - 300px)',
          minHeight: '600px',
          overflowY: 'scroll',
          '& .fc-event': {
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            },
          },
        },
      }}
    >
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'bottom',
        }}
      >
        <div className="flex items-center gap-x-4">
          <ButtonGroup
            size="small"
            sx={{
              '& .MuiButton-root': {
                textTransform: 'none',
                px: 2,
                py: 1,
                height: 36,
                borderColor: theme.palette.divider,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}08`,
                },
              },
            }}
          >
            <Button onClick={() => handleNavigate('today')}>Today</Button>
            <Button onClick={() => handleNavigate('prev')}>
              <ChevronLeft size={16} />
            </Button>
            <Button onClick={() => handleNavigate('next')}>
              <ChevronRight size={16} />
            </Button>
          </ButtonGroup>
          <Typography variant="body1" sx={{ fontWeight: 400 }}>
            {title}
          </Typography>
        </div>
        <div className="flex items-center gap-x-4">
          {role !== 'client_user' && (
            <Box sx={{ width: 240 }}>
              <ResourceSelect
                selectedResources={selectedResource?.toString() ?? null}
                onChange={value => {
                  if (onResourceChange) {
                    onResourceChange(value ? parseInt(value) : undefined);
                  }
                }}
              />
            </Box>
          )}
          <DatePicker
            label="Jump to a Date"
            value={selectedDate}
            onChange={handleDateChange}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            renderInput={() => (
              <Button
                ref={buttonRef}
                variant="outlined"
                color="primary"
                onClick={() => setOpen(true)}
                className="normal-case gap-x-1.5"
              >
                <CalendarDays size={16} /> Select Date
              </Button>
            )}
            PopperProps={{
              anchorEl: buttonRef.current,
              placement: 'bottom-start',
            }}
          />

          <ToggleButtonGroup
            value={currentView}
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 3,
                height: 36,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
              },
            }}
          >
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="day">Day</ToggleButton>
          </ToggleButtonGroup>
        </div>
      </Box>

      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={VIEW_MAPPING[currentView]}
        datesSet={dateInfo => setTitle(dateInfo.view.title)}
        initialDate={selectedDate}
        headerToolbar={false}
        events={allEvents}
        eventDidMount={info => {
          info.el.setAttribute('title', info.event.title);
          if (info.event.extendedProps.meetingLink) {
            const titleEl = info.el.querySelector('.fc-event-title');
            if (titleEl) {
              const icon = document.createElement('span');
              icon.innerHTML = ' 📹';
              icon.style.fontSize = '0.75em';
              titleEl.appendChild(icon);
            }
          }
        }}
        eventClick={handleEventClick}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        weekends={true}
        nowIndicator={true}
        height="auto"
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        allDaySlot={false}
        slotDuration="00:30:00"
        stickyHeaderDates={true}
        select={handleSelectSlot}
      />
      <ExternalEventDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        selectedEvent={selectedEvent}
        methods={methods}
        selectedResourceId={selectedResourceId}
        customerSearch={customerSearch}
        onCustomerSearchChange={setCustomerSearch}
        customers={customers}
        customersLoading={customersLoading}
        selectedCustomerForExternal={selectedCustomerForExternal}
        onCustomerSelect={customer => {
          setSelectedCustomerForExternal(customer);
          setValue('customerId', customer.id);
        }}
        onCreateBooking={async data => {
          try {
            await handleCreateBooking(data);

            setNotification({
              open: true,
              message: 'Booking created successfully from external event',
              severity: SEVERITY.SUCCESS,
            });

            handleCloseDrawer();
          } catch (error) {
            console.error('Error creating booking:', error);
            setNotification({
              open: true,
              message: 'Failed to create booking from external event',
              severity: SEVERITY.ERROR,
            });
            throw error;
          }
        }}
        createLoading={createLoading}
      />
      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Paper>
  );
};

export default BookingCalendar;
